import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Button } from "@trillion/ui/components/ui/button";
import { SortableHeader } from "@trillion/ui/components/admin/sortable-header";
import { sortableInstant, sortableText } from "@trillion/ui/lib/data-grid-sort";
import { Play } from "lucide-react";
import { Link } from "react-router-dom";
import { jobDetailsPath } from "@/lib/job-details";
import type { RecurringJob } from "@/lib/jobs-types";

// Last run / Next run are UTC instants from the API (Kind=Utc ⇒ a trailing Z), rendered in the VIEWER's
// local zone. `timeZoneName: "short"` names that zone, so a local "Next run" can't be misread as the
// schedule's own UTC time. Passing only timeZoneName leaves ECMA-402's date/time defaults in place, so the
// full date and time still render.
export function formatInstant(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString(undefined, { timeZoneName: "short" });
}

interface RecurringColumnActions {
  onRunNow: (job: RecurringJob) => void;
  isTriggering: (id: string) => boolean;
  isPending: (id: string) => boolean;
}

export function getRecurringColumns(
  actions: RecurringColumnActions
): ColumnDef<RecurringJob>[] {
  return [
    {
      accessorKey: "id",
      header: ({ column }) => <SortableHeader column={column} label="Job" />,
      sortingFn: "text",
      // The job's details page (sc-114201). The row navigates too, so the link stops propagation to keep one
      // click one navigation.
      cell: ({ row }) => (
        <Link
          to={jobDetailsPath(row.original.id)}
          className="font-medium underline-offset-4 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.original.id}
        </Link>
      ),
    },
    {
      // The schedule's OWN zone rides with the expression an operator edits — never the viewer's.
      // When schedule_description is null nothing is rendered above the code block: the raw expression
      // stands alone, with no placeholder that could read as a description.
      //
      // Sorted on what the cell shows — the description, falling back to the raw cron — which needs an
      // accessorFn (the column was keyed on `cron` before, so its id changes cron → schedule).
      id: "schedule",
      accessorFn: (job) => sortableText(job.schedule_description ?? job.cron),
      header: ({ column }) => <SortableHeader column={column} label="Schedule" />,
      sortUndefined: "last",
      sortingFn: "text",
      cell: ({ row }) => {
        const { cron, schedule_description, time_zone_id } = row.original;
        return (
          <div className="flex flex-col gap-1">
            {schedule_description ? <span className="text-sm">{schedule_description}</span> : null}
            <span className="text-xs text-muted-foreground">
              <code className="rounded bg-muted px-1.5 py-0.5">{cron}</code>
              {time_zone_id ? <span className="ml-1.5">{time_zone_id}</span> : null}
            </span>
          </div>
        );
      },
    },
    {
      id: "queue",
      accessorFn: (job) => sortableText(job.queue || "default"),
      header: ({ column }) => <SortableHeader column={column} label="Queue" />,
      sortingFn: "text",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.queue || "default"}</span>
      ),
    },
    {
      // Sorts on FETCHED data only. The pending overlay below is cell-render-only, so a row that was
      // just triggered holds its sort position instead of jumping to a "pending" one.
      id: "last_job_state",
      accessorFn: (job) => sortableText(job.last_job_state),
      header: ({ column }) => <SortableHeader column={column} label="Last state" />,
      sortUndefined: "last",
      sortingFn: "text",
      cell: ({ row }) => {
        const job = row.original;
        // Both disjuncts are required: isTriggering covers the POST round-trip (nothing is marked
        // pending until the 202), isPending covers the Hangfire race after it. Together they mean the
        // previous run's terminal state is never shown in the interim.
        if (actions.isTriggering(job.id) || actions.isPending(job.id)) {
          return <Badge variant="outline">Pending</Badge>;
        }
        return job.last_job_state ? (
          <Badge variant="outline">{job.last_job_state}</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "last_execution",
      accessorFn: (job) => sortableInstant(job.last_execution),
      header: ({ column }) => <SortableHeader column={column} label="Last run" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{formatInstant(row.original.last_execution)}</span>
      ),
    },
    {
      id: "next_execution",
      accessorFn: (job) => sortableInstant(job.next_execution),
      header: ({ column }) => <SortableHeader column={column} label="Next run" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{formatInstant(row.original.next_execution)}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-right">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              // The row opens the details page on click; Run now must not also navigate.
              e.stopPropagation();
              actions.onRunNow(row.original);
            }}
            disabled={actions.isTriggering(row.original.id)}
          >
            <Play className="size-4" />
            Run now
          </Button>
        </div>
      ),
    },
  ];
}
